import { useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { MSkeleton } from '@Components/Base';
import { RequestListSearchResponse } from '@Types/api/requestList';
import RequestType from '@Types/common/RequestTypes';
import { RequestListContext } from '@Components/types/RequestListContext';
import { NoData } from '@Features/MaterialRequest/requests/components/NoData';
import { TenderDTO } from '@Types/api/form/tenderRequest/TenderDTO';

import useSmartMapper from './hook/useSmartMapper';
import Card from './components/Card';
import styles from './request-list-mobile.module.css';

type ListRequests = RequestListSearchResponse<TenderDTO>;

type RequestsDataList = Array<TenderDTO>;

export interface RequestListMobileProps {
  listRequests?: ListRequests;
  requestsDataList?: RequestsDataList;
  requestType: RequestType;
  onView: (recordKey: string) => void;
  onInitiate: (recordKey: string, prFor: string) => void;
  onCancel: (recordKey: string) => void;
  onDelete: (recordKey: string) => void;
  onVendors: (recordKey: string) => void;
  onComparison: (recordKey: string, taskId?: string) => void;
  onPrevious: (recordKey: string) => void;
  onUpdate: (recordKey: string) => void;
  onExtention: (requestNo: string) => void;
  onApprovalHistory?: (requestNo: string) => void;
  onLoadMore?: (nextPage?: number) => void;
  showAction?: boolean;
  context?: RequestListContext;
  isFiltersApplied?: boolean;
  isSearchApplied?: boolean;
}

const RequestListMobile = ({
  listRequests,
  requestsDataList,
  requestType,
  showAction = true,
  onLoadMore,
  onView,
  onVendors,
  onComparison,
  onPrevious,
  onUpdate,
  onExtention,
  onCancel,
  onDelete,
  onInitiate,
  onApprovalHistory,
  context = 'my-requests',
  isFiltersApplied = false,
  isSearchApplied = false,
}: RequestListMobileProps): JSX.Element | null => {
  const { transformItem } = useSmartMapper(requestType);
  const [nextPage, setNextPage] = useState(1);

  if (
    !listRequests?.content ||
    !listRequests?.content?.length ||
    !requestsDataList?.length
  ) {
    return (
      <NoData
        context={context}
        isFiltersApplied={isFiltersApplied}
        isSearchApplied={isSearchApplied}
      />
    );
  }

  return (
    <div className={styles.requestListMobile}>
      <InfiniteScroll
        next={() => {
          setNextPage(nextPage + 1);
          onLoadMore?.(nextPage);
        }}
        hasMore={requestsDataList.length < listRequests?.totalElements}
        loader={<MSkeleton paragraph={{ rows: 1 }} active />}
        dataLength={requestsDataList.length}
        scrollableTarget="scrollableList"
        endMessage={
          <p style={{ textAlign: 'center', textTransform: 'uppercase' }}>
            No more data
          </p>
        }
      >
        {requestsDataList.map((item) => {
          const requisitionItem = item as TenderDTO;

          const requestItemId = requisitionItem.id;

          const {
            requestType: requestTypeValue,
            status,
            items,
          } = transformItem(item);

          return (
            <Card
              key={requestItemId}
              requestType={requestTypeValue}
              status={status}
              prFor={item.prfor}
              requestNo={requestItemId}
              items={items}
              showAction={showAction}
              onView={() => {
                onView?.(String(requestItemId));
              }}
              onVendors={() => onVendors(String(requestItemId))}
              onComparison={(recordKey: string, taskId: string) =>
                onComparison(recordKey, taskId)
              }
              onPrevious={() => onPrevious(String(requestItemId))}
              onUpdate={() => onUpdate(String(requestItemId))}
              onExtention={() => onExtention(String(requestItemId))}
              onCancel={() => onCancel?.(String(requestItemId))}
              onDelete={() => onDelete?.(String(requestItemId))}
              onInitiate={() =>
                onInitiate?.(String(requestItemId), item.prForDesc)
              }
              onApprovalHistory={() =>
                onApprovalHistory?.(String(requestItemId))
              }
            />
          );
        })}
      </InfiniteScroll>
    </div>
  );
};

export default RequestListMobile;
